import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError } from "@/lib/api";
import { decideRequest } from "@/lib/approvals";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { PlaneTakeoff, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

// Cancelling an approved leave credits the days back, so it is admin-only and
// always asks first - an accidental click here would silently change someone's
// entitlement.
async function cancelApprovedLeave(l, onDone) {
  const label = `${l.employee_name || "this employee"}: ${l.days} day(s) of ${l.leave_type?.name || "leave"}`;
  if (!window.confirm(`Cancel ${label}?\n\nThe days will be credited back to their balance.`)) return;
  const reason = window.prompt("Reason for cancelling (optional)") || undefined;
  try {
    const r = await api.post(`/leaves/${l.id}/cancel`, { reason });
    const b = r.data?.balance_now || {};
    toast.success(`Cancelled · ${r.data?.days_credited} day(s) credited back (now ${b.balance} left)`);
    onDone?.();
  } catch (e) { toast.error(formatApiError(e)); }
}

const statusStyle = (s) => ({
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-zinc-100 text-zinc-600 border-zinc-200",
}[s] || "bg-zinc-100 text-zinc-600");

export default function LeavesPage({ embedded = false }) {
  const { user } = useAuth();
  const [balances, setBalances] = useState([]);
  const [mine, setMine] = useState([]);
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const isMgr = user?.role === "admin" || !!user?.manages;

  const load = async () => {
    try {
      const calls = [];
      if (user?.employee_id) calls.push(api.get("/leaves/balances").then((r) => setBalances(r.data)));
      if (user?.employee_id) calls.push(api.get("/leaves/my").then((r) => setMine(r.data)));
      if (isMgr) calls.push(api.get("/leaves/pending").then((r) => setPending(r.data)));
      if (isMgr) calls.push(api.get("/leaves/all").then((r) => setAll(r.data)));
      await Promise.all(calls);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [user]);

  const decide = async (id, decision) => {
    try {
      await decideRequest("leave", id, decision === "approved");
      toast.success(`Leave ${decision}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        {!embedded && (
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Leaves</h1>
          <p className="mt-1 text-sm text-zinc-500">Balances, requests and approvals — all in one place.</p>
        </div>
        )}
        {user?.employee_id && <ApplyLeaveDialog onDone={load} />}
      </div>

      {user?.employee_id && balances?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {balances.map((b) => (
            <Card key={b.id} className="border-zinc-200 shadow-none">
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-zinc-500 font-medium">{b.leave_type?.name || b.leave_type_id}</div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-bold text-zinc-900">{b.balance}</span>
                  <span className="text-xs text-zinc-500">/ {b.allocated} days</span>
                </div>
                <div className="mt-1 text-[11px] text-zinc-500">Used: {b.used}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue={user?.employee_id ? "mine" : "pending"}>
        <TabsList className="bg-transparent p-0 border-b border-zinc-200 rounded-none h-auto">
          {user?.employee_id && <TabsTrigger value="mine" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-my-leaves">My leaves</TabsTrigger>}
          {isMgr && <TabsTrigger value="pending" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-pending-leaves">Pending approvals</TabsTrigger>}
          {isMgr && <TabsTrigger value="all" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-emerald-600 data-[state=active]:text-emerald-700 data-[state=active]:shadow-none px-4 py-2" data-testid="tab-all-leaves">All requests</TabsTrigger>}
        </TabsList>

        {user?.employee_id && (
          <TabsContent value="mine" className="mt-6">
            <LeaveTable rows={mine} />
          </TabsContent>
        )}
        {isMgr && (
          <TabsContent value="pending" className="mt-6">
            <LeaveTable rows={pending} showActions onDecide={decide} showEmployee />
          </TabsContent>
        )}
        {isMgr && (
          <TabsContent value="all" className="mt-6">
            <LeaveTable rows={all} showEmployee isAdmin={user?.role === "admin"} onCancel={(l) => cancelApprovedLeave(l, load)} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function LeaveTable({ rows, showActions, onDecide, showEmployee, isAdmin, onCancel }) {
  return (
    <Card className="border-zinc-200 shadow-none">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {showEmployee && <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Employee</TableHead>}
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Type</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500">From</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500">To</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Days</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Reason</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
              {(showActions || isAdmin) && <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((l) => (
              <TableRow key={l.id} className="border-b border-zinc-100 hover:bg-zinc-50/60" data-testid={`leave-row-${l.id}`}>
                {showEmployee && <TableCell className="text-sm font-medium">{l.employee_name}</TableCell>}
                <TableCell className="text-sm">{l.leave_type?.name || l.leave_type_id}</TableCell>
                <TableCell className="text-sm">{dayjs(l.from_date).format("D MMM")}</TableCell>
                <TableCell className="text-sm">{dayjs(l.to_date).format("D MMM")}</TableCell>
                <TableCell className="text-sm text-right tabular-nums">{l.days}</TableCell>
                <TableCell className="text-sm max-w-xs truncate text-zinc-600">{l.reason || "—"}</TableCell>
                <TableCell><Badge variant="outline" className={`text-[11px] ${statusStyle(l.status)}`}>{l.status}</Badge></TableCell>
                {(showActions || isAdmin) && (
                  <TableCell className="text-right">
                    {l.status === "pending" && showActions ? (
                      <div className="inline-flex gap-2">
                        <Button size="sm" onClick={() => onDecide(l.id, "approved")} className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid={`leave-approve-${l.id}`}><Check className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => onDecide(l.id, "rejected")} className="h-8 border-zinc-300" data-testid={`leave-reject-${l.id}`}><X className="h-4 w-4" /></Button>
                      </div>
                    ) : l.status === "approved" && isAdmin ? (
                  <Button size="sm" variant="outline"
                    className="h-8 border-zinc-300 text-xs"
                    data-testid={`leave-cancel-${l.id}`}
                    onClick={() => onCancel(l)}>Cancel</Button>
                ) : <span className="text-xs text-zinc-400">—</span>}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={(showActions || isAdmin) ? 8 : 7} className="text-center text-sm text-zinc-500 py-8">No leave requests. Enjoy the calm.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}

function ApplyLeaveDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ leave_type_id: "", from_date: dayjs().format("YYYY-MM-DD"), to_date: dayjs().format("YYYY-MM-DD"), reason: "", half_day: false });
  const [bal, setBal] = useState([]);
  useEffect(() => {
    api.get("/leave-types").then((r) => setTypes(r.data));
    api.get("/leaves/balances").then((r) => setBal(r.data)).catch(() => {});
  }, []);

  // Tell the employee what they can take BEFORE they fill the form in. The
  // backend refuses an over-limit request anyway, but being stopped at the
  // point of asking is kinder than being bounced after submitting.
  const picked = bal.find((x) => x.leave_type_id === form.leave_type_id);
  const left = picked ? Number(picked.balance) : null;
  const reqDays = (() => {
    if (!form.from_date || !form.to_date) return 0;
    const d1 = new Date(form.from_date);
    const d2 = new Date(form.to_date);
    if (isNaN(d1) || isNaN(d2) || d2 < d1) return 0;
    return form.half_day ? 0.5 : Math.round((d2 - d1) / 86400000) + 1;
  })();
  const overBalance = left !== null && reqDays > 0 && reqDays > left;

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leaves", form); toast.success("Leave applied"); setOpen(false); onDone?.(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-emerald-600 hover:bg-emerald-700 press-scale" data-testid="apply-leave-btn"><PlaneTakeoff className="h-4 w-4 mr-2" /> Apply for leave</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-display text-xl">Apply for leave</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Leave type</Label>
            <Select value={form.leave_type_id} onValueChange={(v) => setForm({ ...form, leave_type_id: v })}>
              <SelectTrigger className="h-9" data-testid="leave-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">From</Label><Input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value })} className="h-9" required data-testid="leave-from-date" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={form.to_date} onChange={(e) => setForm({ ...form, to_date: e.target.value })} className="h-9" required data-testid="leave-to-date" /></div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="half" checked={form.half_day} onCheckedChange={(v) => setForm({ ...form, half_day: !!v })} />
            <Label htmlFor="half" className="text-sm">Half-day</Label>
          </div>
          <div><Label className="text-xs">Reason</Label><Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="leave-reason" /></div>
          {picked && (
              <p className={overBalance ? "text-xs text-red-600 bg-red-50 border border-red-200 rounded-md p-2" : "text-xs text-zinc-500"}>
                {overBalance
                  ? `You have ${left} day(s) of ${picked.leave_type?.name || "this leave"} left, but this request is for ${reqDays}. Shorten the dates or choose another leave type.`
                  : `${left} day(s) available${reqDays > 0 ? ` \u00b7 this request uses ${reqDays}` : ""}`}
              </p>
            )}
            <DialogFooter><Button type="submit" disabled={overBalance} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="apply-leave-submit">Submit</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
